import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { copyToClipboard } from "@toothfairy/shared-ui/ClipboardUtils";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { TouchableOpacity, View } from "react-native";
import envConfig from "../../../../envConfig";

const API_ONLY_MODES = ["planner", "computer", "voice"];

const LANGUAGES = [
  { id: "curl", label: "cURL" },
  { id: "python", label: "Python" },
  { id: "javascript", label: "JavaScript" },
  { id: "rust", label: "Rust" },
];

// The chat widget ships with its own fixed light/dark palettes
// (shared-ui themeProvider) — the preview must mirror the WIDGET theme,
// not the theme of the builder app hosting the preview.
const WIDGET_PALETTES = {
  light: {
    backgroundColor: "#ffffff",
    lightGrey: "rgba(239, 239, 239, 1)",
    font_color: "black",
    darkGrey: "rgba(87, 87, 87, 1)",
    mediumGrey: "rgba(98, 98, 98, 1)",
    primary: "rgba(29, 184, 198, 1)",
    primary_soft: "rgba(29, 184, 198, 0.05)",
  },
  dark: {
    backgroundColor: "rgba(32, 32, 33, 1)",
    lightGrey: "#272727",
    font_color: "white",
    darkGrey: "rgba(203, 203, 203, 1)",
    mediumGrey: "rgba(98, 98, 98, 1)",
    primary: "rgba(29, 184, 198, 1)",
    primary_soft: "rgba(29, 184, 198, 0.1)",
  },
};

const hexToRGB = (hex, alpha) => {
  if (!hex) return undefined;
  let expandedHex = hex;
  if (hex?.length === 4) {
    expandedHex = "#" + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
  }
  const r = parseInt(expandedHex.slice(1, 3), 16);
  const g = parseInt(expandedHex.slice(3, 5), 16);
  const b = parseInt(expandedHex.slice(5, 7), 16);
  if ([r, g, b].some((v) => Number.isNaN(v))) return undefined;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const maskApiKey = (key) => {
  if (!key) return "";
  if (key.length <= 8) return "•".repeat(key.length);
  return `${key.slice(0, 4)}${"•".repeat(12)}${key.slice(-4)}`;
};

const buildStreamingSnippets = ({
  path,
  workspaceId,
  apiKey,
  agentId,
  isAgent,
}) => {
  const base = envConfig?.REACT_APP_AIS_ENDPOINT;
  const url = `${base}${path}`;
  const curlBody = `'{\\n    "workspaceid": "${workspaceId}",\\n    "agentid": "${agentId}",\\n    "messages": [{ "role": "user", "content": "Hello!" }]${
    isAgent ? ',\\n    "raw_stream": true' : ""
  }\\n  }'`;
  return {
    curl: `curl -N -X POST ${url} \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: ${apiKey}" \\
  -d ${curlBody}`,
    python: `import requests

with requests.post(
    "${url}",
    headers={
        "Content-Type": "application/json",
        "x-api-key": "${apiKey}",
    },
    json={
        "workspaceid": "${workspaceId}",
        "agentid": "${agentId}",
        "messages": [{"role": "user", "content": "Hello!"}]${
          isAgent ? ',\n        "raw_stream": True' : ""
        },
    },
    stream=True,
) as response:
    response.raise_for_status()
    for line in response.iter_lines(decode_unicode=True):
        if line:
            print(line)  # SSE event: data: {...}`,
    javascript: `const response = await fetch("${url}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "x-api-key": "${apiKey}",
  },
  body: JSON.stringify({
    workspaceid: "${workspaceId}",
    agentid: "${agentId}",
    messages: [{ role: "user", content: "Hello!" }]${
      isAgent ? ",\n    raw_stream: true" : ""
    },
  }),
});

if (!response.ok) throw new Error(\`HTTP \${response.status}\`);

const reader = response.body.getReader();
const decoder = new TextDecoder();
while (true) {
  const { done, value } = await reader.read();
  if (done) break;
  process.stdout.write(decoder.decode(value, { stream: true }));
}`,
    rust: `use reqwest::header::{HeaderMap, HeaderValue, CONTENT_TYPE};
use serde_json::json;

#[tokio::main]
async fn main() -> Result<(), Box<dyn std::error::Error>> {
    let mut headers = HeaderMap::new();
    headers.insert(CONTENT_TYPE, HeaderValue::from_static("application/json"));
    headers.insert("x-api-key", HeaderValue::from_str("${apiKey}")?);

    let body = json!({
        "workspaceid": "${workspaceId}",
        "agentid": "${agentId}",
        "messages": [{ "role": "user", "content": "Hello!" }]${
          isAgent ? ',\n        "raw_stream": true' : ""
        }
    });

    let mut response = reqwest::Client::new()
        .post("${url}")
        .headers(headers)
        .json(&body)
        .send()
        .await?;
    response.error_for_status_ref()?;

    while let Some(chunk) = response.chunk().await? {
        print!("{}", String::from_utf8_lossy(&chunk));
    }
    Ok(())
}`,
  };
};

const buildVoiceSnippets = ({ workspaceId, apiKey, agentId }) => {
  const endpoint = envConfig?.REACT_APP_LIVEKIT_TOKEN_ENDPOINT;
  return {
    curl: `curl -X POST ${endpoint} \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: ${apiKey}" \\
  -d '{\\n    "workspace_id": "${workspaceId}",\\n    "chat_id": "<chat_id>",\\n    "agent_id": "${agentId}"\\n  }'`,
    python: `import requests

response = requests.post(
    "${endpoint}",
    headers={
        "Content-Type": "application/json",
        "x-api-key": "${apiKey}",
    },
    json={
        "workspace_id": "${workspaceId}",
        "chat_id": "<chat_id>",
        "agent_id": "${agentId}",
    },
)
response.raise_for_status()
session = response.json()
# session["token"], session["url"], session["room_name"]
print(session)`,
    javascript: `const response = await fetch("${endpoint}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "x-api-key": "${apiKey}",
  },
  body: JSON.stringify({
    workspace_id: "${workspaceId}",
    chat_id: "<chat_id>",
    agent_id: "${agentId}",
  }),
});

if (!response.ok) throw new Error(\`HTTP \${response.status}\`);
const { token, url, room_name } = await response.json();
// Connect to the LiveKit server at \`url\` with \`token\` (room: room_name)`,
    rust: `use reqwest::header::{HeaderMap, HeaderValue, CONTENT_TYPE};
use serde_json::{json, Value};

#[tokio::main]
async fn main() -> Result<(), Box<dyn std::error::Error>> {
    let mut headers = HeaderMap::new();
    headers.insert(CONTENT_TYPE, HeaderValue::from_static("application/json"));
    headers.insert("x-api-key", HeaderValue::from_str("${apiKey}")?);

    let body = json!({
        "workspace_id": "${workspaceId}",
        "chat_id": "<chat_id>",
        "agent_id": "${agentId}"
    });

    let session: Value = reqwest::Client::new()
        .post("${endpoint}")
        .headers(headers)
        .json(&body)
        .send()
        .await?
        .error_for_status()?
        .json()
        .await?;

    // session["token"], session["url"], session["room_name"]
    println!("{}", session);
    Ok(())
}`,
  };
};

// Region-aware default endpoints used by the SDK/client snippets.
// The au/eu/us hosts are documented on https://toothfairyai.com/en/developers.
const sdkRegionHosts = (() => {
  const ais =
    envConfig?.REACT_APP_AIS_ENDPOINT || "https://ais.toothfairyai.com";
  const region = ais.includes(".eu.")
    ? "eu"
    : ais.includes(".us.")
    ? "us"
    : "au";
  const host = (prefix) =>
    region === "au"
      ? `https://${prefix}.toothfairyai.com`
      : `https://${prefix}.${region}.toothfairyai.com`;
  return {
    region,
    baseUrl: host("api"),
    aiUrl: host("ai"),
    aiStreamUrl: ais,
  };
})();

const buildSdkSnippets = ({ agentId, apiKey, workspaceId }) => {
  const { baseUrl, aiUrl, aiStreamUrl } = sdkRegionHosts;
  return {
    python: `pip install toothfairyai

from toothfairyai import ToothFairyClient

client = ToothFairyClient(
    api_key="${apiKey}",
    workspace_id="${workspaceId}",
    base_url="${baseUrl}",
    ai_url="${aiUrl}",
    ai_stream_url="${aiStreamUrl}",
)

# Stream a reply from your agent
stream = client.streaming.send_to_agent(
    message="Hello, how can you help me?",
    agent_id="${agentId}",
)
for event in stream:
    if event.is_token:
        print(event.text, end="", flush=True)
    elif event.is_complete:
        print()

# Or get the full response at once
response = client.chat.send_to_agent(
    message="Hello, how can you help me?",
    agent_id="${agentId}",
)
print(response.agent_response)`,
    node: `npm install @toothfairyai/sdk

import { ToothFairyClient } from "@toothfairyai/sdk";

const client = new ToothFairyClient({
  apiKey: "${apiKey}",
  workspaceId: "${workspaceId}",
  baseUrl: "${baseUrl}",
  aiUrl: "${aiUrl}",
  aiStreamUrl: "${aiStreamUrl}",
});

// Stream a reply from your agent
const session = await client.streaming.sendToAgent(
  "Hello, how can you help me?",
  "${agentId}",
);
session.on("data", (data) => process.stdout.write(data.text));
session.on("complete", () => console.log());

// Or get the full response at once
const response = await client.chat.sendToAgent(
  "Hello, how can you help me?",
  "${agentId}",
);
console.log(response.agentResponse);`,
  };
};

const buildCliSnippets = ({ agentId, apiKey, workspaceId }) => {
  return {
    node: `# Install the CLI (Node.js)
npm install -g @toothfairyai/cli

# Authenticate once
tf auth login --api-key "${apiKey}"

# Stream a reply from your agent
tf chat "${agentId}" --workspace "${workspaceId}" --message "Hello, how can you help me?" --stream

# Run the same agent from npx without a global install
npx @toothfairyai/cli chat "${agentId}" --workspace "${workspaceId}" --message "Hello, how can you help me?" --stream`,
    python: `# The CLI is also available for Python
pip install toothfairy-cli

# Authenticate once
toothfairy auth login --api-key "${apiKey}"

# Stream a reply from your agent
toothfairy chat "${agentId}" --workspace "${workspaceId}" --message "Hello, how can you help me?" --stream`,
  };
};

const getApiConfig = ({ mode, workspaceId, apiKey, agentId, t }) => {
  if (mode === "voice") {
    const endpoint = envConfig?.REACT_APP_LIVEKIT_TOKEN_ENDPOINT;
    return {
      endpoint: `POST ${endpoint}`,
      docsUrl: "https://apidocs.toothfairyai.com/#post-/token",
      description: t(
        "settings.qa.apiPanelVoiceDesc",
        "Generate a LiveKit access token to start a WebRTC voice session with this agent."
      ),
      snippets: buildVoiceSnippets({ workspaceId, apiKey, agentId }),
    };
  }
  if (mode === "planner" || mode === "computer") {
    return {
      endpoint: `POST ${envConfig?.REACT_APP_AIS_ENDPOINT}/planner`,
      docsUrl: "https://apidocs.toothfairyai.com/#post-/planner",
      description: t(
        "settings.qa.apiPanelPlannerDesc",
        "Stream this orchestrator agent over Server-Sent Events, including plan updates and step execution events."
      ),
      snippets: buildStreamingSnippets({
        path: "/planner",
        workspaceId,
        apiKey,
        agentId,
        isAgent: false,
      }),
    };
  }
  return {
    endpoint: `POST ${envConfig?.REACT_APP_AIS_ENDPOINT}/agent`,
    docsUrl: "https://apidocs.toothfairyai.com/#post-/agent",
    description: t(
      "settings.qa.apiPanelAgentDesc",
      "Stream this agent's responses over Server-Sent Events. Filter events by the `replying` status to render text as it streams."
    ),
    snippets: buildStreamingSnippets({
      path: "/agent",
      workspaceId,
      apiKey,
      agentId,
      isAgent: true,
    }),
  };
};

// Faithful replica of the chatWidget AgentScreen open-chat state
// (chatWidget/src/Pages/Agent/AgentScreen.js), using the widget's own palette
// and ToothFairyAI default branding when no logo/colors are configured.
const WidgetPreview = ({
  agentTitle,
  disclaimer,
  placeholder,
  showSplash,
  agentLogo,
  showAgentName,
  agentColor,
  logoBackground,
  agentImage,
  quickQuestions,
  messageOnLaunch,
  forcedTheme,
  showWidgetControls,
}) => {
  const palette = WIDGET_PALETTES[forcedTheme === "dark" ? "dark" : "light"];
  const accent = agentColor || palette.primary;
  const headerBg = logoBackground || palette.lightGrey;
  const showDarkModeToggle = !["light", "dark"].includes(forcedTheme);
  const defaultHeaderLogo = require("../../../assets/logoTitle.png");
  const defaultAgentIcon = require("../../../assets/favicon.png");

  const quickQuestionsList =
    typeof quickQuestions === "string"
      ? quickQuestions
          .split(";")
          .map((q) => q.trim().replace(/\s+/g, " "))
          .filter((q) => q !== "")
          .slice(0, 3)
      : [];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        borderRadius: 12,
        border: `1px solid ${palette.lightGrey}`,
        backgroundColor: palette.backgroundColor,
        overflow: "hidden",
        fontFamily: "normalFont, sans-serif",
        maxHeight: 550,
      }}
    >
      {/* Header — .tf-agent-header */}
      <div
        style={{
          height: 60,
          backgroundColor: headerBg,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        {agentLogo ? (
          <img
            src={agentLogo}
            style={{
              height: 50,
              width: "auto",
              alignSelf: "flex-start",
              marginTop: 2,
              marginLeft: 2,
              objectFit: "contain",
            }}
            alt=""
          />
        ) : (
          <img
            src={defaultHeaderLogo}
            style={{
              width: 152,
              height: 36,
              objectFit: "contain",
              marginLeft: 5,
            }}
            alt=""
          />
        )}
        {showWidgetControls !== false && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 5,
              marginRight: 5,
            }}
          >
            <AppIcon
              icon={AppAssets?.icons?.minimise}
              size={14}
              color={accent}
            />
            <AppIcon icon={AppAssets?.icons?.close} size={16} color={accent} />
          </div>
        )}
      </div>

      {/* Chat area — AppQuantumChat widget state */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          padding: "5px 5px 0 5px",
          minHeight: 220,
          overflowY: "auto",
        }}
      >
        {showSplash ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 20px",
            }}
          >
            <img
              src={agentImage || defaultAgentIcon}
              style={{ width: 48, height: 48, borderRadius: 20 }}
              alt=""
            />
            <span
              style={{
                fontWeight: "bold",
                fontSize: 16,
                marginTop: 10,
                color: palette.darkGrey,
                textAlign: "center",
              }}
            >
              {messageOnLaunch || agentTitle}
            </span>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              marginTop: 7,
            }}
          >
            <img
              src={agentImage || defaultAgentIcon}
              style={{
                width: 26,
                height: 26,
                borderRadius: 20,
                marginRight: 4,
                flexShrink: 0,
              }}
              alt=""
            />
            <div
              style={{
                borderRadius: 10,
                backgroundColor: "transparent",
                padding: "8px 12px",
                maxWidth: "90%",
              }}
            >
              {showAgentName && (
                <span
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: "bold",
                    color: palette.mediumGrey,
                    marginTop: 2,
                  }}
                >
                  {agentTitle}
                </span>
              )}
              <span
                style={{
                  display: "block",
                  fontSize: 13,
                  lineHeight: "20px",
                  color: palette.darkGrey,
                }}
              >
                {messageOnLaunch ||
                  "I'm a ToothFairyAI agent, how can I help you?"}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Quick questions — shown while the chat is empty */}
      {quickQuestionsList.length > 0 && (
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            marginTop: 10,
            marginBottom: 5,
            gap: 6,
            paddingRight: 5,
          }}
        >
          {quickQuestionsList.map((question, index) => (
            <span
              key={index}
              style={{
                padding: "5px 10px 5px 5px",
                borderRadius: 5,
                backgroundColor:
                  hexToRGB(agentColor, 0.1) || palette.primary_soft,
                color: accent,
                fontSize: 11,
                marginLeft: 5,
                maxWidth: 350,
                textAlign: "end",
                cursor: "pointer",
                overflowWrap: "break-word",
              }}
            >
              {question}
            </span>
          ))}
        </div>
      )}

      {/* Composer — .tf-chat-input */}
      <div style={{ padding: "0 5px" }}>
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 20px",
            borderRadius: 10,
            backgroundColor: palette.lightGrey,
            boxSizing: "border-box",
          }}
        >
          <span
            style={{
              fontSize: 13,
              color: palette.darkGrey,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {placeholder || "Ask me anything"}
          </span>
          <AppIcon
            icon={AppAssets?.icons?.send}
            size={20}
            color={accent}
            wrapperStyle={{ marginLeft: 10 }}
          />
        </div>
      </div>

      {/* Footer — ToothairyAIDisclosure */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 15,
          marginTop: 8,
          flexShrink: 0,
        }}
      >
        <AppIcon
          icon={AppAssets?.icons?.reload}
          color={accent}
          size={14}
          wrapperStyle={{ marginLeft: 5, cursor: "pointer" }}
        />
        <span
          style={{
            fontSize: 11,
            color: palette.darkGrey,
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
            maxWidth: "80%",
            padding: "0 10px",
          }}
        >
          {disclaimer || "This AI agent is powered by ToothFairyAI"}
        </span>
        {showDarkModeToggle ? (
          <AppIcon
            icon={AppAssets?.icons?.theme_adjust}
            color={accent}
            size={16}
            wrapperStyle={{ marginRight: 5, cursor: "pointer" }}
          />
        ) : (
          <span style={{ width: 21 }} />
        )}
      </div>
    </div>
  );
};

const AgentWidgetPreview = ({
  isEdit,
  mode,
  agentId,
  workspaceId,
  apiKey,
  agentLink,
  widgetCode,
  agentImage,
  agentLogo,
  agentTitle,
  disclaimer,
  placeholderInputMessage,
  messageOnLaunch,
  showSplashWhenEmpty,
  showAgentName,
  color,
  darkColor,
  logoBackground,
  darkLogoBackground,
  quickQuestions,
  forcedTheme,
  showWidgetControls,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const AppToasty = AppToast.useToast();
  const [panel, setPanel] = useState("widget");
  const [language, setLanguage] = useState("curl");
  const [apiSection, setApiSection] = useState("rest");
  const [sdkLang, setSdkLang] = useState("python");
  const [cliLang, setCliLang] = useState("node");
  const [previewTheme, setPreviewTheme] = useState(null);
  const primary = currentTheme?.theme?.primary;

  const isApiOnly = API_ONLY_MODES.includes(mode);
  const panels = [
    ...(isApiOnly
      ? []
      : [
          { id: "widget", icon: "widget", fallback: "Widget view" },
          { id: "code", icon: "html", fallback: "Widget code" },
        ]),
    { id: "api", icon: "api", fallback: "Programmatic integration" },
  ];
  const activePanel = panels.some((p) => p.id === panel)
    ? panel
    : panels[0]?.id;

  const effectivePreviewTheme =
    previewTheme ||
    (["light", "dark"].includes(forcedTheme) ? forcedTheme : "light");

  const apiConfig = getApiConfig({ mode, workspaceId, apiKey, agentId, t });
  const sdkSnippets = buildSdkSnippets({ agentId, apiKey, workspaceId });
  const cliSnippets = buildCliSnippets({ agentId, apiKey, workspaceId });

  const maskedApiKey = maskApiKey(apiKey);
  const redactSnippet = (code) =>
    apiKey && code ? code.split(apiKey).join(maskedApiKey) : code;

  const handleCopySnippet = async () => {
    const success = await copyToClipboard(apiConfig.snippets[language]);
    AppToasty.show(
      success ? t("toasts.clipboard.copied") : t("toasts.clipboard.copyError"),
      { type: success ? "success" : "danger" }
    );
  };

  const handleCopySdkSnippet = async () => {
    const success = await copyToClipboard(sdkSnippets[sdkLang]);
    AppToasty.show(
      success ? t("toasts.clipboard.copied") : t("toasts.clipboard.copyError"),
      { type: success ? "success" : "danger" }
    );
  };

  const handleCopyCliSnippet = async () => {
    const success = await copyToClipboard(cliSnippets[cliLang]);
    AppToasty.show(
      success ? t("toasts.clipboard.copied") : t("toasts.clipboard.copyError"),
      { type: success ? "success" : "danger" }
    );
  };

  const handleCopyWidgetCode = async () => {
    const success = await copyToClipboard(widgetCode);
    AppToasty.show(
      success
        ? t("toasts.clipboard.htmlCopied")
        : t("toasts.clipboard.copyError"),
      { type: success ? "success" : "danger" }
    );
  };

  const renderSaveFirstHint = () => (
    <View
      style={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        padding: 30,
      }}
    >
      <AppIcon
        icon={AppAssets.icons.widget}
        size={26}
        color={currentTheme?.theme?.mediumGrey}
      />
      <AppText
        style={{ marginTop: 12, textAlign: "center" }}
        color={currentTheme?.theme?.mediumGrey}
      >
        {t(
          "settings.qa.widgetPreviewSaveFirst",
          "Save the agent to preview and share the widget"
        )}
      </AppText>
    </View>
  );

  const renderCodeCard = ({ code, tabs, activeTab, onSelectTab, onCopy }) => (
    <View
      style={{
        marginTop: 14,
        borderRadius: 10,
        overflow: "hidden",
        backgroundColor: "#0B1220",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingLeft: 6,
          paddingRight: 10,
          paddingVertical: 6,
          backgroundColor: "#111A2E",
        }}
      >
        {tabs ? (
          tabs.map((l) => {
            const isActive = activeTab === l.id;
            return (
              <TouchableOpacity
                key={l.id}
                onPress={() => onSelectTab?.(l.id)}
                style={{
                  paddingVertical: 5,
                  paddingHorizontal: 10,
                  borderRadius: 6,
                  marginRight: 2,
                  backgroundColor: isActive
                    ? "rgba(148, 163, 184, 0.18)"
                    : "transparent",
                }}
              >
                <AppText
                  style={{ fontSize: 12 }}
                  fontWeight={isActive ? "600" : "normal"}
                  color={isActive ? "#E2E8F0" : "#94A3B8"}
                >
                  {l.label}
                </AppText>
              </TouchableOpacity>
            );
          })
        ) : (
          <AppText
            style={{ fontSize: 12, paddingHorizontal: 4 }}
            fontWeight="600"
            color="#94A3B8"
          >
            {"HTML"}
          </AppText>
        )}
        <View style={{ flex: 1 }} />
        <AppIcon
          icon={AppAssets?.icons?.quickCopy}
          color="#94A3B8"
          isClickable
          onPress={onCopy}
        />
      </View>
      <pre
        style={{
          display: "block",
          margin: 0,
          padding: "14px 16px",
          overflowX: "auto",
          maxHeight: 420,
          backgroundColor: "transparent",
        }}
      >
        <code
          style={{
            display: "block",
            fontFamily:
              "'SF Mono', 'Fira Code', 'Fira Mono', Menlo, Consolas, monospace",
            fontSize: 12,
            lineHeight: "20px",
            color: "#E2E8F0",
            whiteSpace: "pre",
          }}
        >
          {code}
        </code>
      </pre>
    </View>
  );

  const apiSections = [
    { id: "rest", label: t("settings.qa.apiSection.rest", "REST API") },
    { id: "sdk", label: t("settings.qa.apiSection.sdk", "SDK") },
    { id: "cli", label: t("settings.qa.apiSection.cli", "CLI") },
  ];

  const renderApiSectionTabs = () => (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 4,
      }}
    >
      {apiSections.map((s) => {
        const isActive = apiSection === s.id;
        return (
          <TouchableOpacity
            key={s.id}
            onPress={() => setApiSection(s.id)}
            style={{
              paddingVertical: 6,
              paddingHorizontal: 12,
              borderRadius: 7,
              marginRight: 6,
              backgroundColor: isActive
                ? currentTheme?.theme?.primary_soft || "rgba(99, 102, 241, 0.1)"
                : currentTheme?.theme?.lightGrey,
            }}
          >
            <AppText
              style={{ fontSize: 12 }}
              color={isActive ? primary : currentTheme?.theme?.font_color}
              fontWeight={isActive ? "600" : "normal"}
            >
              {s.label}
            </AppText>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  const renderApiPanel = () => (
    <View style={{ flex: 1, paddingTop: 4 }}>
      <AppText fontWeight="bold" style={{ fontSize: 14 }}>
        {apiConfig.endpoint}
      </AppText>
      <AppText
        style={{ marginTop: 6, fontSize: 13, lineHeight: 19 }}
        color={currentTheme?.theme?.darkGrey}
      >
        {apiConfig.description}
      </AppText>

      {renderApiSectionTabs()}

      {apiSection === "rest" &&
        renderCodeCard({
          code: redactSnippet(apiConfig.snippets[language]),
          tabs: LANGUAGES,
          activeTab: language,
          onSelectTab: setLanguage,
          onCopy: handleCopySnippet,
        })}

      {apiSection === "sdk" &&
        renderCodeCard({
          code: redactSnippet(sdkSnippets[sdkLang]),
          tabs: [
            { id: "python", label: "Python" },
            { id: "node", label: "Node.js" },
          ],
          activeTab: sdkLang,
          onSelectTab: setSdkLang,
          onCopy: handleCopySdkSnippet,
        })}

      {apiSection === "cli" &&
        renderCodeCard({
          code: redactSnippet(cliSnippets[cliLang]),
          tabs: [
            { id: "node", label: "Node.js" },
            { id: "python", label: "Python" },
          ],
          activeTab: cliLang,
          onSelectTab: setCliLang,
          onCopy: handleCopyCliSnippet,
        })}
      <AppText
        wrapperStyle={{
          alignSelf: "flex-start",
          marginTop: 10,
          paddingVertical: 8,
          paddingHorizontal: 2,
          gap: 5,
        }}
        leftIcon={AppAssets?.icons?.link}
        leftIconStyle={{ marginLeft: 0, marginRight: 12 }}
        style={{
          color: primary,
          fontWeight: "bold",
          fontSize: 13,
        }}
        textStyle={{ marginLeft: 0 }}
        iconSize={13}
        isClickable
        onPress={() => window.open(apiConfig.docsUrl, "_blank")}
      >
        {t("settings.qa.apiPanelDocsLink", "View API docs")}
      </AppText>
    </View>
  );

  const renderCodePanel = () => (
    <View style={{ flex: 1, paddingTop: 4 }}>
      <AppText fontWeight="bold" style={{ fontSize: 14 }}>
        {t("settings.qa.addAgentToWebsite")}
      </AppText>
      <AppText
        style={{ marginTop: 6, fontSize: 13, lineHeight: 19 }}
        color={currentTheme?.theme?.darkGrey}
      >
        {t("settings.qa.copyPasteCodeInstructions")}
      </AppText>
      {renderCodeCard({
        code: widgetCode,
        onCopy: handleCopyWidgetCode,
      })}
    </View>
  );

  const renderThemeToggle = () => {
    const themes = [
      { id: "light", label: t("settings.qa.themeLight", "Light") },
      { id: "dark", label: t("settings.qa.themeDark", "Dark") },
    ];
    return (
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          alignSelf: "flex-end",
          marginBottom: 10,
          backgroundColor: currentTheme?.theme?.lightGrey,
          borderRadius: 8,
          padding: 3,
        }}
      >
        {themes.map((th) => {
          const isActive = effectivePreviewTheme === th.id;
          return (
            <TouchableOpacity
              key={th.id}
              onPress={() => setPreviewTheme(th.id)}
              style={{
                paddingVertical: 4,
                paddingHorizontal: 12,
                borderRadius: 6,
                backgroundColor: isActive
                  ? currentTheme?.theme?.primary_soft ||
                    "rgba(99, 102, 241, 0.1)"
                  : "transparent",
              }}
            >
              <AppText
                style={{ fontSize: 12 }}
                color={isActive ? primary : currentTheme?.theme?.darkGrey}
                fontWeight={isActive ? "600" : "normal"}
              >
                {th.label}
              </AppText>
            </TouchableOpacity>
          );
        })}
      </View>
    );
  };

  const renderPreview = () => {
    if (activePanel === "widget") {
      return (
        <View style={{ flex: 1 }}>
          {renderThemeToggle()}
          <WidgetPreview
            agentTitle={agentTitle}
            disclaimer={disclaimer}
            placeholder={placeholderInputMessage}
            showSplash={showSplashWhenEmpty}
            agentLogo={agentLogo}
            showAgentName={showAgentName}
            agentColor={
              effectivePreviewTheme === "dark" ? darkColor || color : color
            }
            logoBackground={
              effectivePreviewTheme === "dark"
                ? darkLogoBackground || logoBackground
                : logoBackground
            }
            agentImage={agentImage}
            quickQuestions={quickQuestions}
            messageOnLaunch={messageOnLaunch}
            forcedTheme={effectivePreviewTheme}
            showWidgetControls={showWidgetControls}
          />
        </View>
      );
    }
    if (!isEdit) return renderSaveFirstHint();
    if (activePanel === "code") return renderCodePanel();
    return renderApiPanel();
  };

  return (
    <View
      style={{
        width: 420,
        minWidth: 360,
        flex: 1,
        marginLeft: isApiOnly ? 0 : 16,
        borderLeftWidth: isApiOnly ? 0 : 1,
        borderLeftColor: currentTheme?.theme?.border_color,
        paddingLeft: isApiOnly ? 0 : 16,
        alignSelf: "stretch",
      }}
    >
      <View style={{ marginBottom: 12 }}>
        {panels.length > 1 &&
          panels.map((p) => {
            const isActive = activePanel === p.id;
            return (
              <TouchableOpacity
                key={p.id}
                onPress={() => setPanel(p.id)}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  paddingVertical: 7,
                  paddingHorizontal: 10,
                  borderRadius: 8,
                  marginBottom: 2,
                  backgroundColor: isActive
                    ? currentTheme?.theme?.primary_soft ||
                      "rgba(99, 102, 241, 0.1)"
                    : currentTheme?.theme?.transparent,
                }}
              >
                <AppIcon
                  icon={AppAssets.icons[p.icon]}
                  size={14}
                  color={isActive ? primary : currentTheme?.theme?.darkGrey}
                  style={{ marginRight: 9 }}
                />
                <AppText
                  style={{ fontSize: 13 }}
                  color={isActive ? primary : currentTheme?.theme?.font_color}
                  fontWeight={isActive ? "600" : "normal"}
                >
                  {t(`settings.qa.widgetPanel.${p.id}`, p.fallback)}
                </AppText>
              </TouchableOpacity>
            );
          })}
        {!isApiOnly && isEdit && agentLink && (
          <TouchableOpacity
            onPress={() => window.open(agentLink, "_blank")}
            style={{
              flexDirection: "row",
              alignItems: "center",
              paddingVertical: 7,
              paddingHorizontal: 10,
              borderRadius: 8,
              marginTop: 4,
              borderTopWidth: 1,
              borderTopColor: currentTheme?.theme?.lightGrey,
            }}
          >
            <AppIcon
              icon={AppAssets.icons.link}
              size={14}
              color={primary}
              style={{ marginRight: 9 }}
            />
            <AppText style={{ fontSize: 13 }} color={primary} fontWeight="600">
              {t("settings.qa.widgetPanel.openWidget", "Open widget")}
            </AppText>
          </TouchableOpacity>
        )}
      </View>
      <View style={{ flex: 1 }}>{renderPreview()}</View>
    </View>
  );
};

export default AgentWidgetPreview;
